import { ADMIN_PAGES_KEYS } from '../../../../i18n/constants/keys';
import { jobOpeningDetailsTabs, jobOpeningPages, jobOpeningTypes } from '../../../../utils/types/JobOpenings';
import { JOB_OPENING_TYPE } from '../../Constants';
import { ICompleteMappings, IPageElementMap, IStageConfig } from '../interfaces';

/** Motion spring config for dialog animations */
export const SPRING_CONFIG = {
  type: 'spring' as const,
  stiffness: 400,
  damping: 30,
  mass: 0.8,
};

/** i18n keys for page tab labels */
export const PAGE_LABEL_KEYS: Record<string, string> = {
  'Job Opening Details': ADMIN_PAGES_KEYS.jobOpenings.pages.jobOpeningDetails,
  'Applicant Analysis': ADMIN_PAGES_KEYS.jobOpenings.pages.applicantAnalysis,
  'Auto-Assessments': ADMIN_PAGES_KEYS.jobOpenings.pages.autoAssessments,
  'Smart Interviews': ADMIN_PAGES_KEYS.jobOpenings.pages.smartInterviews,
};

/** Tab pages displayed in the pill-style tabs */
export const TAB_PAGE_LABELS: jobOpeningPages[] = [
  'Job Opening Details',
  'Applicant Analysis',
  'Auto-Assessments',
  'Smart Interviews',
  'Psychometric',
  'Wellness',
];

/** Maps each page to required completed tabs before it becomes enabled */
export const COMPLETE_MAPPINGS: Record<jobOpeningPages, jobOpeningDetailsTabs[]> = {
  'Job Opening Details': [],
  'Auto-Assessments': ['Auto Assess Questions'],
  'Applicant Analysis': ['Job Requisition', 'Job Description', 'CV Ingestion'],
  'Smart Interviews': ['Job Requisition', 'Job Description', 'CV Ingestion'],
  Analytics: [],
  Psychometric: [],
  Wellness: [],
};

/** Maps page name to the job opening element that must exist */
export const PAGE_ELEMENT_MAP: Record<jobOpeningPages, jobOpeningTypes> = {
  'Job Opening Details': 'Job Opening Details',
  'Auto-Assessments': 'Job Opening Details',
  'Applicant Analysis': 'Applicant Analysis',
  'Smart Interviews': 'Smart Interviews',
  Psychometric: 'Applicant Analysis',
  Analytics: 'Job Opening Details',
  Wellness: 'Wellness',
};

/** Pages that are always enabled regardless of element state */
export const ALWAYS_ENABLED_PAGES: jobOpeningPages[] = [
  'Job Opening Details',
  'Applicant Analysis',
  'Psychometric',
  'Wellness',
  'Smart Interviews',
];

/** Job opening types that are always hidden in the create dialog counter */
export const ALWAYS_HIDDEN_TYPES = [JOB_OPENING_TYPE.JOB_OPENING_DETAILS];

/** Job opening types that cannot be toggled off in the create dialog */
export const DISABLED_TYPES = [
  JOB_OPENING_TYPE.JOB_OPENING_DETAILS,
  JOB_OPENING_TYPE.APPLICANT_ANALYSIS,
];

/** Job opening types selected by default in the create dialog */
export const DEFAULT_SELECTED_TYPES = [
  JOB_OPENING_TYPE.JOB_OPENING_DETAILS,
  JOB_OPENING_TYPE.APPLICANT_ANALYSIS,
];

/** Default stage definitions for the progress tracker */
export const DEFAULT_STAGES: IStageConfig[] = [
  {
    label: 'Job Description',
    status: 'Inactive',
    currentSubStageIndex: 0,
    subStages: [
      { label: 'Inactive', status: 'Inactive' },
      { label: 'Pending', status: 'Pending' },
      { label: 'Completed', status: 'Completed' },
    ],
  },
  {
    label: 'Job Post',
    status: 'Inactive',
    currentSubStageIndex: 0,
    subStages: [
      { label: 'Inactive', status: 'Inactive' },
      { label: 'Pending', status: 'Pending' },
      { label: 'In Progress', status: 'In Progress' },
      { label: 'Completed', status: 'Completed' },
    ],
  },
  {
    label: 'Shortlisting',
    status: 'Inactive',
    currentSubStageIndex: 0,
    subStages: [
      { label: 'Inactive', status: 'Inactive' },
      { label: 'In Progress', status: 'In Progress' },
      { label: 'Completed', status: 'Completed' },
    ],
  },
  {
    label: 'Interviews',
    status: 'Inactive',
    currentSubStageIndex: 0,
    subStages: [
      { label: 'Inactive', status: 'Inactive' },
      { label: 'In Progress', status: 'In Progress' },
      { label: 'Completed', status: 'Completed' },
    ],
  },
  {
    label: 'Decision Making',
    status: 'Inactive',
    currentSubStageIndex: 0,
    subStages: [
      { label: 'Inactive', status: 'Inactive' },
      { label: 'Decision in Progress', status: 'Decision in Progress' },
      { label: 'Offer in Progress', status: 'Offer in Progress' },
      { label: 'Completed', status: 'Completed' },
    ],
  },
  {
    label: 'Closed',
    status: 'Inactive',
    currentSubStageIndex: 0,
    subStages: [
      { label: 'Inactive', status: 'Inactive' },
      { label: 'Offer Accepted', status: 'Offer Accepted' },
      { label: 'No Suitable Candidate', status: 'No Suitable Candidate' },
      { label: 'No Offers Accepted', status: 'No Offers Accepted' },
      { label: 'Job Request Withdrawn', status: 'Job Request Withdrawn' },
    ],
  },
];

/** Status-to-stage label map for syncing API status to stages */
export const STATUS_KEY_TO_LABEL: Record<string, string> = {
  jobDescription: 'Job Description',
  jobPost: 'Job Post',
  shortlisting: 'Shortlisting',
  interviews: 'Interviews',
  decisionMaking: 'Decision Making',
  closed: 'Closed',
};
